import { AlertTriangle, X, RotateCw } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { photosApi } from '../../api/photos';
import styles from './CorruptPhotosModal.module.css';

export interface CorruptPhoto {
  photoId: string;
  filename: string;
}

interface CorruptPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: CorruptPhoto[];
  refixing: boolean;
  onRefix: () => void;
}

function extOf(name: string): string {
  const m = name.match(/\.([^.]+)$/);
  return m ? m[1].toUpperCase() : 'FILE';
}

/**
 * Lists the photos the ZIP builder had to skip because their processed
 * (overlay) file was unreadable. The originals are fine — hence the reassurance
 * copy and the "re-fix" (re-composite) action. Structured header / scrollable
 * grid / pinned footer so the action never overlaps the photos.
 */
export function CorruptPhotosModal({ isOpen, onClose, photos, refixing, onRefix }: CorruptPhotosModalProps) {
  const count = photos.length;

  // File-type breakdown — surfaces patterns (e.g. "mostly HEIF/.HIF"), which is
  // usually the actual reason a batch of photos failed to process.
  const byExt = photos.reduce<Record<string, number>>((acc, p) => {
    const e = extOf(p.filename);
    acc[e] = (acc[e] ?? 0) + 1;
    return acc;
  }, {});
  const chips = Object.entries(byExt).sort((a, b) => b[1] - a[1]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className={styles['wrap']} role="dialog" aria-modal="true" aria-label="Photos left out of the download">
        <header className={styles['header']}>
          <span className={styles['headerIcon']} aria-hidden>
            <AlertTriangle size={19} strokeWidth={2.2} />
          </span>
          <span className={styles['headerText']}>
            <h2 className={styles['title']}>Photos left out of the download</h2>
            <span className={styles['subtitle']}>
              {count} photo{count !== 1 ? 's' : ''} · not in the ZIP
            </span>
          </span>
          <button type="button" className={styles['close']} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <p className={styles['explain']}>
          <strong>Your originals are safe.</strong> The edited (overlay) copy of these photos couldn't
          be read, so they were skipped in the download ZIP. Re-fixing re-creates the edited copy from
          the original and adds them back automatically.
        </p>

        {chips.length > 0 && (
          <div className={styles['chips']}>
            {chips.map(([ext, n]) => (
              <span key={ext} className={styles['chip']}>{n} · {ext}</span>
            ))}
          </div>
        )}

        <div className={styles['body']}>
          <div className={styles['grid']}>
            {photos.map((p) => (
              <div key={p.photoId} className={styles['cell']}>
                <div className={styles['thumbWrap']}>
                  <img
                    className={styles['thumb']}
                    src={photosApi.thumbnailUrl(p.photoId)}
                    alt={p.filename}
                    loading="lazy"
                  />
                  <span className={styles['badge']} aria-hidden>
                    <AlertTriangle size={11} strokeWidth={2.5} />
                  </span>
                </div>
                <span className={styles['name']} title={p.filename}>{p.filename}</span>
              </div>
            ))}
          </div>
        </div>

        <footer className={styles['footer']}>
          <span className={styles['footerNote']}>Re-fixing runs in the background.</span>
          <span className={styles['footerActions']}>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            <Button variant="primary" size="sm" onClick={onRefix} loading={refixing}>
              {!refixing && <RotateCw size={14} style={{ marginRight: 6 }} />}
              Re-fix {count} photo{count !== 1 ? 's' : ''}
            </Button>
          </span>
        </footer>
      </div>
    </Modal>
  );
}
